import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

// Use window.location.origin for API calls to work on any domain
const API = `${window.location.origin}/api`;

export const EditSpendingModal = ({ open, spending, onClose, onSuccess }) => {
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (spending) {
      setAmount(spending.amount.toString());
      setDescription(spending.description);
    }
  }, [spending]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!amount || !description) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API}/spending/${spending.spending_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          amount: parseFloat(amount),
          description
        })
      });

      if (response.ok) {
        toast.success('Spending updated');
        onSuccess?.();
      } else {
        throw new Error('Failed to update spending');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setAmount('');
    setDescription('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-[#1B1F3A] border-[#2D3354] text-white max-w-sm mx-auto z-50">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Edit Spending</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4 pb-4">
          <div className="space-y-2">
            <Label className="text-[#9CA3AF]">Amount</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]">£</span>
              <Input
                type="number"
                inputMode="decimal"
                step="0.01"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="pl-7 h-12 bg-[#252A4A] border-[#2D3354] text-white text-lg placeholder:text-[#6B7280] focus:border-[#63E6E2]"
                data-testid="edit-spending-amount-input"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label className="text-[#9CA3AF]">Description</Label>
            <Input
              placeholder="e.g., Coffee, Groceries"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="h-12 bg-[#252A4A] border-[#2D3354] text-white placeholder:text-[#6B7280] focus:border-[#63E6E2]"
              data-testid="edit-spending-description-input"
            />
          </div>
          
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1 h-12 border-[#2D3354] text-white hover:bg-white/5"
              data-testid="cancel-edit-spending-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 h-12 bg-[#63E6E2] text-[#0F1224] hover:bg-[#4DD4CF] font-semibold"
              data-testid="save-spending-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-[#0F1224]/30 border-t-[#0F1224] rounded-full animate-spin" />
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditSpendingModal;
